import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getLevelInfo } from '../data/levels';
import { normalizeSelectedBrands } from '../data/brands';
import { supabase, supabaseProjectHost } from '../lib/supabase';
import { useAuth } from './useAuth';
import { setPendingChallengeScanContext } from '../utils/challengeScanContext';
import { devLog } from '../utils/devLog';

const GUEST_PROGRESS_KEY = 'colobongo_progress_guest_v2';
const USER_CACHE_KEY_PREFIX = 'colobongo_progress_user_cache_';
const MIGRATION_FLAGS_KEY = 'colobongo_progress_migration_flags_v1';
const GUEST_ONBOARDING_LOCK_KEY = 'colobongo_onboarding_guest_done_v1';
const USER_ONBOARDING_LOCK_PREFIX = 'colobongo_onboarding_user_done_';
const CLOUD_HYDRATION_TIMEOUT_MS = 7000;
const HYDRATION_WATCHDOG_TIMEOUT_MS = 9500;
const AUTH_LOADING_WATCHDOG_TIMEOUT_MS = 12000;
const UNVERIFIED_CLOUD_RETRY_MS = 12000;
const ADD_XP_RPC_NAME = 'add_user_xp';
const ADD_DAILY_XP_RPC_NAME = 'add_daily_user_xp';
const DAILY_XP_TIMEZONE = 'America/Toronto';
const LOG_READ = '[PROGRESS_READ]';
const LOG_SAVE = '[PROGRESS_SAVE]';
const LOG_CLOUD = '[PROGRESS_CLOUD]';
const LOG_LEVEL = '[PROGRESS_LEVEL]';
const LOG_DB_READ = '[PROGRESS_DB_READ]';
const LOG_DB_WRITE = '[PROGRESS_DB_WRITE]';
const LOG_XP_GAIN = '[PROGRESS_XP_GAIN]';
const LOG_APPLY = '[PROGRESS_APPLY]';
const LOG_RESOLVE = '[PROGRESS_RESOLVE]';
const LOG_UI_STATE = '[PROGRESS_UI_STATE]';
const LOG_PROGRESS_HYDRATION = '[PROGRESS_HYDRATION]';

const defaultProgress = {
  xp: 0,
  level: 1,
  challengesCompleted: 0,
  currentStreak: 0,
  longestStreak: 0,
  badges: [],
  completedChallenges: [],
  activeChallenge: null,
  selectedBrands: [],
  skillLevel: 'intermediate',
  aiMode: 'real',
  lastLoginDate: null,
  lastDailyXPDate: null,
  onboardingComplete: false,
  _leveledUp: false,
  _newLevel: null,
};

function isBrowser() {
  return typeof window !== 'undefined';
}

function readStorageJson(key, fallback) {
  if (!isBrowser()) return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch (_error) {
    return fallback;
  }
}

function writeStorageJson(key, value) {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (_error) {
    // best effort persistence
  }
}

function getOnboardingLockKey(userId) {
  return userId ? `${USER_ONBOARDING_LOCK_PREFIX}${userId}` : GUEST_ONBOARDING_LOCK_KEY;
}

function readOnboardingLock(userId) {
  if (!isBrowser()) return false;
  try {
    return window.localStorage.getItem(getOnboardingLockKey(userId)) === 'true';
  } catch (_error) {
    return false;
  }
}

// Exported so App.js can short-circuit the onboarding gate without waiting for cloud hydration
export { readOnboardingLock, getOnboardingLockKey };

function writeOnboardingLock(userId, enabled) {
  if (!isBrowser()) return;
  const key = getOnboardingLockKey(userId);
  try {
    if (enabled) {
      window.localStorage.setItem(key, 'true');
      return;
    }
    window.localStorage.removeItem(key);
  } catch (_error) {
    // best effort persistence
  }
}

function normalizeArray(value, fallback = []) {
  return Array.isArray(value) ? value : fallback;
}

function withTimeout(promise, ms, label = 'timeout') {
  let timerId;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timerId = setTimeout(() => reject(new Error(label)), ms);
    }),
  ]).finally(() => {
    if (timerId) clearTimeout(timerId);
  });
}

function nowIso() {
  return new Date().toISOString();
}

function getDailyToken() {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: DAILY_XP_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  } catch (_error) {
    return new Date().toISOString().slice(0, 10);
  }
}

function parseDateLoose(value) {
  if (!value || typeof value !== 'string') return 0;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeProgress(rawProgress) {
  const raw = rawProgress && typeof rawProgress === 'object' ? rawProgress : {};
  const merged = {
    ...defaultProgress,
    ...raw,
  };

  const xp = Number.isFinite(Number(merged.xp)) ? Math.max(0, Math.round(Number(merged.xp))) : 0;

  return {
    xp,
    level: getLevelInfo(xp).currentLevel.level,
    challengesCompleted: Number.isFinite(Number(merged.challengesCompleted))
      ? Math.max(0, Math.round(Number(merged.challengesCompleted)))
      : 0,
    currentStreak: Number.isFinite(Number(merged.currentStreak))
      ? Math.max(0, Math.round(Number(merged.currentStreak)))
      : 0,
    longestStreak: Number.isFinite(Number(merged.longestStreak))
      ? Math.max(0, Math.round(Number(merged.longestStreak)))
      : 0,
    badges: normalizeArray(merged.badges),
    completedChallenges: normalizeArray(merged.completedChallenges),
    activeChallenge: merged.activeChallenge || null,
    selectedBrands: normalizeSelectedBrands(normalizeArray(merged.selectedBrands)),
    skillLevel: typeof merged.skillLevel === 'string' && merged.skillLevel ? merged.skillLevel : 'intermediate',
    aiMode: 'real',
    lastLoginDate: merged.lastLoginDate || null,
    lastDailyXPDate: merged.lastDailyXPDate || null,
    onboardingComplete: Boolean(merged.onboardingComplete),
    _leveledUp: Boolean(merged._leveledUp),
    _newLevel: merged._newLevel || null,
  };
}

function isMeaningfulProgress(progress) {
  const value = normalizeProgress(progress);
  return (
    value.xp > 0 ||
    value.challengesCompleted > 0 ||
    value.currentStreak > 0 ||
    value.longestStreak > 0 ||
    value.onboardingComplete ||
    value.completedChallenges.length > 0
  );
}

function hasOnboardingCompletionSignals(progress) {
  const value = normalizeProgress(progress);
  return (
    value.selectedBrands.length > 0 ||
    value.challengesCompleted > 0 ||
    value.completedChallenges.length > 0 ||
    Boolean(value.activeChallenge) ||
    value.currentStreak > 0 ||
    value.longestStreak > 0 ||
    value.xp > 0 ||
    Boolean(value.lastLoginDate) ||
    Boolean(value.lastDailyXPDate)
  );
}

function rowToProgress(row) {
  if (!row || typeof row !== 'object') return normalizeProgress(defaultProgress);

  const xp = Number.isFinite(Number(row.xp)) ? Math.max(0, Math.round(Number(row.xp))) : 0;
  const computedLevel = getLevelInfo(xp).currentLevel.level;
  const storedLevel = Number.isFinite(Number(row.level)) ? Math.max(1, Math.round(Number(row.level))) : null;
  if (storedLevel !== null && storedLevel !== computedLevel) {
    devLog.warn(`${LOG_LEVEL} Cloud level mismatch detected, using XP-derived level`, {
      xp,
      storedLevel,
      computedLevel,
    });
  }

  return normalizeProgress({
    xp,
    level: row.level,
    challengesCompleted: row.challenges_completed,
    currentStreak: row.current_streak,
    longestStreak: row.longest_streak,
    badges: row.badges,
    completedChallenges: row.completed_challenges,
    activeChallenge: row.active_challenge,
    selectedBrands: row.selected_brands,
    skillLevel: row.skill_level,
    aiMode: row.ai_mode,
    lastLoginDate: row.last_login_date,
    lastDailyXPDate: row.last_daily_xp_date,
    onboardingComplete: row.onboarding_complete,
  });
}

function progressToRow(progress, userId) {
  const value = normalizeProgress(progress);

  return {
    user_id: userId,
    xp: value.xp,
    level: value.level,
    challenges_completed: value.challengesCompleted,
    current_streak: value.currentStreak,
    longest_streak: value.longestStreak,
    badges: value.badges,
    completed_challenges: value.completedChallenges,
    active_challenge: value.activeChallenge,
    selected_brands: value.selectedBrands,
    skill_level: value.skillLevel,
    ai_mode: value.aiMode,
    last_login_date: value.lastLoginDate,
    last_daily_xp_date: value.lastDailyXPDate,
    onboarding_complete: value.onboardingComplete,
  };
}

function progressSignature(progress) {
  const value = normalizeProgress(progress);
  return JSON.stringify({
    xp: value.xp,
    level: value.level,
    challengesCompleted: value.challengesCompleted,
    currentStreak: value.currentStreak,
    longestStreak: value.longestStreak,
    badges: value.badges,
    completedChallenges: value.completedChallenges,
    activeChallenge: value.activeChallenge,
    selectedBrands: value.selectedBrands,
    skillLevel: value.skillLevel,
    aiMode: value.aiMode,
    lastLoginDate: value.lastLoginDate,
    lastDailyXPDate: value.lastDailyXPDate,
    onboardingComplete: value.onboardingComplete,
  });
}

export function useUserProgress() {
  const { user, loading: authLoading } = useAuth();
  const userId = user?.id ?? null;
  const [progress, setProgressState] = useState(() => normalizeProgress(defaultProgress));
  const [isSyncing, setIsSyncing] = useState(false);
  const [progressSource, setProgressSource] = useState('local');
  const [isHydrated, setIsHydrated] = useState(false);

  const saveTimeoutRef = useRef(null);
  const refreshInFlightRef = useRef(false);
  const progressRef = useRef(progress);
  const userIdRef = useRef(null);
  const hydrationRunRef = useRef(0);
  const lastSavedSignatureRef = useRef('');
  const lastSavedUserIdRef = useRef(null);
  const cloudVerifiedRef = useRef(false);
  const pendingLocalChangesRef = useRef(false);
  const atomicXpInFlightRef = useRef(false);

  const setProgress = useCallback((updater) => {
    setProgressState((prev) => {
      const next = updater instanceof Function ? updater(prev) : updater;
      const normalizedNext = normalizeProgress(next);
      const prevSignature = progressSignature(prev);
      const nextSignature = progressSignature(normalizedNext);
      const changed = prevSignature !== nextSignature;

      if (changed && userIdRef.current) {
        pendingLocalChangesRef.current = true;
      }

      return normalizedNext;
    });
  }, []);

  useEffect(() => {
    progressRef.current = progress;
  }, [progress]);

  const applyCloudRowToState = useCallback((row, context = {}) => {
    if (!row || typeof row !== 'object') return null;
    const targetUserId = context.userId || userId;
    if (!targetUserId || userIdRef.current !== targetUserId) {
      devLog.warn(`${LOG_RESOLVE} stale_cloud_apply_ignored`, {
        targetUserId,
        currentUserId: userIdRef.current,
        reason: context.reason || 'stale_apply',
        timestamp: nowIso(),
      });
      return null;
    }
    const cloudProgress = rowToProgress(row);
    const cloudSignature = progressSignature(cloudProgress);
    const oldLevel = getLevelInfo(progressRef.current?.xp || 0).currentLevel.level;
    const newLevel = getLevelInfo(cloudProgress.xp).currentLevel.level;
    const leveledUp = newLevel > oldLevel;

    setProgressState({
      ...cloudProgress,
      _leveledUp: leveledUp,
      _newLevel: leveledUp ? getLevelInfo(cloudProgress.xp).currentLevel : null,
    });

    const userCacheKey = `${USER_CACHE_KEY_PREFIX}${targetUserId}`;
    writeStorageJson(userCacheKey, cloudProgress);
    if (cloudProgress.onboardingComplete) {
      writeOnboardingLock(targetUserId, true);
    }
    setProgressSource('cloud');
    setIsHydrated(true);
    cloudVerifiedRef.current = true;
    pendingLocalChangesRef.current = false;
    lastSavedUserIdRef.current = targetUserId;
    lastSavedSignatureRef.current = cloudSignature;

    devLog.info(`${LOG_APPLY} rpc_apply`, {
      userId: targetUserId,
      source: context.source || 'rpc',
      reason: context.reason || 'atomic_write',
      xp: cloudProgress.xp,
      level: cloudProgress.level,
      leveledUp,
      timestamp: nowIso(),
    });

    return cloudProgress;
  }, [userId]);

  const addXpAtomicCloud = useCallback(async (gain, reason = 'addXP') => {
    if (!userId || gain <= 0) return null;

    atomicXpInFlightRef.current = true;
    devLog.info(`${LOG_DB_WRITE} rpc_start`, {
      userId,
      rpc: ADD_XP_RPC_NAME,
      gain,
      reason,
      projectHost: supabaseProjectHost,
      timestamp: nowIso(),
    });
    void supabase.auth.getUser()
      .then(({ data }) => {
        devLog.info(`${LOG_DB_WRITE} rpc_auth_context`, {
          userId,
          rpc: ADD_XP_RPC_NAME,
          authUid: data?.user?.id ?? null,
          sameUser: (data?.user?.id ?? null) === userId,
          timestamp: nowIso(),
        });
      })
      .catch((error) => {
        devLog.warn(`${LOG_DB_WRITE} rpc_auth_context_failed`, {
          userId,
          rpc: ADD_XP_RPC_NAME,
          message: error?.message || 'unknown',
        });
      });

    try {
      const { data, error } = await withTimeout(
        supabase.rpc(ADD_XP_RPC_NAME, {
          p_user_id: userId,
          p_xp_delta: gain,
          p_reason: reason,
        }),
        CLOUD_HYDRATION_TIMEOUT_MS,
        'rpc_add_user_xp_timeout'
      );

      if (error) {
        throw error;
      }

      const row = Array.isArray(data) ? data[0] : data;
      if (userIdRef.current !== userId) {
        devLog.warn(`${LOG_DB_WRITE} rpc_stale_response_ignored`, {
          userId,
          rpc: ADD_XP_RPC_NAME,
          reason,
          currentUserId: userIdRef.current,
          timestamp: nowIso(),
        });
        return null;
      }
      const applied = applyCloudRowToState(row, { source: 'rpc', reason, userId });
      devLog.info(`${LOG_DB_WRITE} rpc_success`, {
        userId,
        rpc: ADD_XP_RPC_NAME,
        reason,
        xp: applied?.xp ?? null,
        level: applied?.level ?? null,
        timestamp: nowIso(),
      });
      return applied;
    } catch (error) {
      devLog.error(`${LOG_DB_WRITE} rpc_error`, {
        userId,
        rpc: ADD_XP_RPC_NAME,
        reason,
        gain,
        code: error?.code || null,
        message: error?.message || 'unknown',
        timestamp: nowIso(),
      });
      return null;
    } finally {
      atomicXpInFlightRef.current = false;
    }
  }, [applyCloudRowToState, userId]);

  const addDailyXpAtomicCloud = useCallback(async (gain, dayToken, reason = 'daily_xp') => {
    if (!userId || gain <= 0) return null;

    atomicXpInFlightRef.current = true;
    devLog.info(`${LOG_DB_WRITE} rpc_start`, {
      userId,
      rpc: ADD_DAILY_XP_RPC_NAME,
      gain,
      reason,
      dayToken,
      projectHost: supabaseProjectHost,
      timestamp: nowIso(),
    });
    void supabase.auth.getUser()
      .then(({ data }) => {
        devLog.info(`${LOG_DB_WRITE} rpc_auth_context`, {
          userId,
          rpc: ADD_DAILY_XP_RPC_NAME,
          authUid: data?.user?.id ?? null,
          sameUser: (data?.user?.id ?? null) === userId,
          timestamp: nowIso(),
        });
      })
      .catch((error) => {
        devLog.warn(`${LOG_DB_WRITE} rpc_auth_context_failed`, {
          userId,
          rpc: ADD_DAILY_XP_RPC_NAME,
          message: error?.message || 'unknown',
        });
      });

    try {
      const { data, error } = await withTimeout(
        supabase.rpc(ADD_DAILY_XP_RPC_NAME, {
          p_user_id: userId,
          p_xp_delta: gain,
          p_day_token: dayToken,
          p_reason: reason,
        }),
        CLOUD_HYDRATION_TIMEOUT_MS,
        'rpc_add_daily_user_xp_timeout'
      );

      if (error) {
        throw error;
      }

      const row = Array.isArray(data) ? data[0] : data;
      if (userIdRef.current !== userId) {
        devLog.warn(`${LOG_DB_WRITE} rpc_stale_response_ignored`, {
          userId,
          rpc: ADD_DAILY_XP_RPC_NAME,
          reason,
          currentUserId: userIdRef.current,
          timestamp: nowIso(),
        });
        return null;
      }
      const applied = applyCloudRowToState(row, { source: 'rpc', reason, userId });
      devLog.info(`${LOG_DB_WRITE} rpc_success`, {
        userId,
        rpc: ADD_DAILY_XP_RPC_NAME,
        reason,
        xp: applied?.xp ?? null,
        level: applied?.level ?? null,
        dayToken,
        timestamp: nowIso(),
      });
      return applied;
    } catch (error) {
      devLog.error(`${LOG_DB_WRITE} rpc_error`, {
        userId,
        rpc: ADD_DAILY_XP_RPC_NAME,
        reason,
        gain,
        dayToken,
        code: error?.code || null,
        message: error?.message || 'unknown',
        timestamp: nowIso(),
      });
      return null;
    } finally {
      atomicXpInFlightRef.current = false;
    }
  }, [applyCloudRowToState, userId]);

  const persistCloudProgress = useCallback(
    async (nextProgress, options = {}) => {
      if (!userId) return false;
      const { force = false, reason = 'unspecified' } = options;

      if (!force && !cloudVerifiedRef.current) {
        devLog.warn(`${LOG_SAVE} Skipped cloud save (cloud not verified yet)`, {
          userId,
          reason,
        });
        return false;
      }

      const signature = progressSignature(nextProgress);
      if (
        lastSavedUserIdRef.current === userId &&
        signature === lastSavedSignatureRef.current
      ) {
        pendingLocalChangesRef.current = false;
        devLog.info(`${LOG_SAVE} Skipped cloud save (same signature)`, {
          userId,
          reason,
        });
        return true;
      }

      devLog.info(`${LOG_SAVE} Cloud save start`, {
        userId,
        reason,
        force,
        xp: nextProgress?.xp,
        level: nextProgress?.level,
        projectHost: supabaseProjectHost,
      });
      devLog.info(`${LOG_DB_WRITE} start`, {
        userId,
        reason,
        xp: nextProgress?.xp,
        level: nextProgress?.level,
        timestamp: nowIso(),
      });

      const payload = progressToRow(nextProgress, userId);
      if (!force && !atomicXpInFlightRef.current) {
        try {
          const { data: currentRow, error: readError } = await withTimeout(
            supabase
              .from('user_progress')
              .select('xp, level')
              .eq('user_id', userId)
              .maybeSingle(),
            CLOUD_HYDRATION_TIMEOUT_MS,
            'progress_pre_upsert_read_timeout'
          );
          if (readError) {
            throw readError;
          }
          const currentXp = Number.isFinite(Number(currentRow?.xp))
            ? Math.max(0, Math.round(Number(currentRow.xp)))
            : null;
          if (currentXp !== null && currentXp > payload.xp) {
            payload.xp = currentXp;
            payload.level = getLevelInfo(currentXp).currentLevel.level;
            devLog.info(`${LOG_SAVE} Pre-upsert guard prevented XP rollback`, {
              userId,
              reason,
              guardedXp: currentXp,
            });
          }
        } catch (error) {
          devLog.warn(`${LOG_SAVE} Pre-upsert guard failed, skipping write to avoid rollback risk`, {
            userId,
            reason,
            error: error?.message || 'unknown',
          });
          return false;
        }
      }
      let response;
      try {
        response = await withTimeout(
          supabase
            .from('user_progress')
            .upsert(payload, { onConflict: 'user_id' })
            .select('user_id, xp, level, updated_at')
            .single(),
          CLOUD_HYDRATION_TIMEOUT_MS,
          'progress_upsert_timeout'
        );
      } catch (error) {
        devLog.error(`${LOG_SAVE} Cloud save timeout/failure`, {
          userId,
          reason,
          error: error?.message || 'unknown',
        });
        devLog.error(`${LOG_DB_WRITE} timeout/failure`, {
          userId,
          reason,
          xp: payload.xp,
          level: payload.level,
          timestamp: nowIso(),
          error: error?.message || 'unknown',
        });
        return false;
      }
      const { data, error } = response || {};

      if (error) {
        devLog.error(`${LOG_SAVE} Cloud save error`, {
          userId,
          reason,
          code: error?.code,
          message: error?.message,
          details: error?.details,
          hint: error?.hint,
        });
        devLog.error(`${LOG_DB_WRITE} error`, {
          userId,
          reason,
          xp: payload.xp,
          level: payload.level,
          timestamp: nowIso(),
          code: error?.code,
          message: error?.message,
          details: error?.details,
          hint: error?.hint,
        });
        const isMissingColumn =
          error?.code === '42703' ||
          /column .* does not exist/i.test(error?.message || '');
        if (!isMissingColumn) {
          return false;
        }

        // Schema fallback: keep persistence alive for XP/level even if optional columns
        // are missing in production. This prevents "no rows ever written" situations.
        const minimalPayload = {
          user_id: userId,
          xp: payload.xp,
          level: getLevelInfo(payload.xp).currentLevel.level,
        };

        try {
          const { data: minimalData, error: minimalError } = await withTimeout(
            supabase
              .from('user_progress')
              .upsert(minimalPayload, { onConflict: 'user_id' })
              .select('user_id, xp, level, updated_at')
              .single(),
            CLOUD_HYDRATION_TIMEOUT_MS,
            'progress_minimal_upsert_timeout'
          );

          if (minimalError) {
            throw minimalError;
          }

          lastSavedSignatureRef.current = signature;
          lastSavedUserIdRef.current = userId;
          pendingLocalChangesRef.current = false;

          devLog.warn(`${LOG_SAVE} Cloud save schema fallback succeeded (minimal payload)`, {
            userId,
            reason,
            xp: minimalData?.xp ?? minimalPayload.xp,
            level: minimalData?.level ?? minimalPayload.level,
          });
          return true;
        } catch (fallbackError) {
          devLog.error(`${LOG_SAVE} Cloud save schema fallback failed`, {
            userId,
            reason,
            code: fallbackError?.code || null,
            message: fallbackError?.message || 'unknown',
            details: fallbackError?.details || null,
            hint: fallbackError?.hint || null,
          });
          return false;
        }
      }

      lastSavedSignatureRef.current = signature;
      lastSavedUserIdRef.current = userId;
      pendingLocalChangesRef.current = false;

      // Align UI with the server-confirmed XP value to eliminate any drift.
      const confirmedXP = data?.xp !== undefined ? Math.max(0, Math.round(Number(data.xp))) : null;
      if (confirmedXP !== null && Number.isFinite(confirmedXP)) {
        setProgressState((prev) => {
          if (prev.xp === confirmedXP) return prev;
          return normalizeProgress({ ...prev, xp: confirmedXP, level: getLevelInfo(confirmedXP).currentLevel.level });
        });
      }

      devLog.info(`${LOG_SAVE} Cloud save success`, {
        userId,
        reason,
        xp: confirmedXP ?? payload.xp,
        level: payload.level,
      });
      devLog.info(`${LOG_DB_WRITE} success`, {
        userId,
        reason,
        xp: confirmedXP ?? payload.xp,
        level: data?.level ?? payload.level,
        updatedAt: data?.updated_at || null,
        timestamp: nowIso(),
      });
      return true;
    },
    [userId]
  );

  const ensureCloudProgressRow = useCallback(
    async (seedProgress, reason = 'ensure_cloud_row') => {
      if (!userId) return { ok: false, method: 'no_user', row: null };

      const seed = normalizeProgress(seedProgress || defaultProgress);
      const basePayload = {
        user_id: userId,
        xp: seed.xp,
        level: getLevelInfo(seed.xp).currentLevel.level,
      };
      const fullPayload = progressToRow(seed, userId);
      const shouldUseFullBootstrapPayload =
        seed.onboardingComplete ||
        seed.selectedBrands.length > 0 ||
        seed.skillLevel !== defaultProgress.skillLevel ||
        seed.aiMode !== defaultProgress.aiMode;
      const bootstrapPayload = shouldUseFullBootstrapPayload ? fullPayload : basePayload;

      try {
        const sessionSnapshot = await withTimeout(
          supabase.auth.getSession(),
          2500,
          'ensure_row_get_session_timeout'
        );
        const sessionUserId = sessionSnapshot?.data?.session?.user?.id ?? null;
        devLog.info(`${LOG_CLOUD} ensure_row_auth_snapshot`, {
          userId,
          sessionUserId,
          sameUser: sessionUserId === userId,
          reason,
          projectHost: supabaseProjectHost,
          timestamp: nowIso(),
        });
      } catch (sessionError) {
        devLog.warn(`${LOG_CLOUD} ensure_row_auth_snapshot_failed`, {
          userId,
          reason,
          message: sessionError?.message || 'unknown',
        });
      }

      try {
        const { data, error } = await withTimeout(
          supabase.rpc(ADD_XP_RPC_NAME, {
            p_user_id: userId,
            p_xp_delta: 0,
            p_reason: `${reason}:rpc_probe`,
          }),
          CLOUD_HYDRATION_TIMEOUT_MS,
          'ensure_row_rpc_timeout'
        );

        if (error) {
          throw error;
        }

        const row = Array.isArray(data) ? data[0] : data;
        const rowProgress = rowToProgress(row || basePayload);
        const needsOnboardingRepair = seed.onboardingComplete && !rowProgress.onboardingComplete;
        if (needsOnboardingRepair) {
          const { data: repairedData, error: repairedError } = await withTimeout(
            supabase
              .from('user_progress')
              .upsert(fullPayload, { onConflict: 'user_id' })
              .select('*')
              .maybeSingle(),
            CLOUD_HYDRATION_TIMEOUT_MS,
            'ensure_row_onboarding_repair_timeout'
          );
          if (!repairedError) {
            devLog.info(`${LOG_DB_WRITE} ensure_row_onboarding_repair_success`, {
              userId,
              reason,
              onboardingComplete: true,
              timestamp: nowIso(),
            });
            return { ok: true, method: 'rpc_add_user_xp_zero_with_onboarding_repair', row: repairedData || row };
          }
          devLog.warn(`${LOG_DB_WRITE} ensure_row_onboarding_repair_failed`, {
            userId,
            reason,
            code: repairedError?.code || null,
            message: repairedError?.message || 'unknown',
            timestamp: nowIso(),
          });
        }
        devLog.info(`${LOG_DB_WRITE} ensure_row_rpc_success`, {
          userId,
          reason,
          hasRow: Boolean(row),
          xp: row?.xp ?? null,
          level: row?.level ?? null,
          timestamp: nowIso(),
        });
        return { ok: true, method: 'rpc_add_user_xp_zero', row: row || null };
      } catch (rpcError) {
        devLog.warn(`${LOG_DB_WRITE} ensure_row_rpc_failed`, {
          userId,
          reason,
          code: rpcError?.code || null,
          message: rpcError?.message || 'unknown',
          details: rpcError?.details || null,
          hint: rpcError?.hint || null,
          timestamp: nowIso(),
        });
      }

      try {
        const { data, error } = await withTimeout(
          supabase
            .from('user_progress')
            .upsert(bootstrapPayload, { onConflict: 'user_id' })
            .select('*')
            .maybeSingle(),
          CLOUD_HYDRATION_TIMEOUT_MS,
          'ensure_row_minimal_upsert_timeout'
        );

        if (error) {
          throw error;
        }

        devLog.info(`${LOG_DB_WRITE} ensure_row_minimal_upsert_success`, {
          userId,
          reason,
          xp: data?.xp ?? bootstrapPayload.xp,
          level: data?.level ?? bootstrapPayload.level,
          onboardingComplete: Boolean(data?.onboarding_complete ?? fullPayload.onboarding_complete),
          usedFullPayload: shouldUseFullBootstrapPayload,
          timestamp: nowIso(),
        });
        return { ok: true, method: shouldUseFullBootstrapPayload ? 'full_upsert' : 'minimal_upsert', row: data || bootstrapPayload };
      } catch (upsertError) {
        devLog.error(`${LOG_DB_WRITE} ensure_row_minimal_upsert_failed`, {
          userId,
          reason,
          code: upsertError?.code || null,
          message: upsertError?.message || 'unknown',
          details: upsertError?.details || null,
          hint: upsertError?.hint || null,
          timestamp: nowIso(),
        });
      }

      return { ok: false, method: 'failed', row: null };
    },
    [userId]
  );

  const hydrateGuestProgress = useCallback((runId, source = 'local') => {
    const guestStored = normalizeProgress(readStorageJson(GUEST_PROGRESS_KEY, defaultProgress));
    const onboardingLocked = readOnboardingLock(null);
    const guest = onboardingLocked && !guestStored.onboardingComplete
      ? normalizeProgress({ ...guestStored, onboardingComplete: true })
      : guestStored;
    if (runId !== hydrationRunRef.current) return;
    setProgressState(guest);
    setProgressSource(source);
    setIsSyncing(false);
    setIsHydrated(true);
    cloudVerifiedRef.current = false;
    if (guest.onboardingComplete) {
      writeOnboardingLock(null, true);
    }
    devLog.info(`${LOG_READ} Guest hydration applied`, {
      runId,
      source,
    });
    devLog.info(`${LOG_APPLY} guest_apply`, {
      runId,
      source,
      xp: guest.xp,
      level: guest.level,
      timestamp: nowIso(),
    });
    devLog.info(`${LOG_PROGRESS_HYDRATION} guest_applied`, {
      runId,
      source,
      onboardingComplete: guest.onboardingComplete,
      onboardingLocked,
      timestamp: nowIso(),
    });
  }, []);

  const hydrateCloudProgress = useCallback(async (runId, options = {}) => {
    if (!userId) return;
    const { silent = false, reason = 'hydrate' } = options;
    let hasCloudRow = false;

    refreshInFlightRef.current = true;
    setIsSyncing(true);
    if (!silent) {
      setIsHydrated(false);
    }

    const userCacheKey = `${USER_CACHE_KEY_PREFIX}${userId}`;
    const cachedUserProgress = normalizeProgress(readStorageJson(userCacheKey, defaultProgress));

    devLog.info(`${LOG_CLOUD} Cloud read start`, {
      runId,
      userId,
      reason,
      silent,
    });
    devLog.info(`${LOG_PROGRESS_HYDRATION} cloud_read_start`, {
      runId,
      userId,
      reason,
      silent,
      timestamp: nowIso(),
    });
    devLog.info(`${LOG_DB_READ} start`, {
      runId,
      userId,
      reason,
      silent,
      projectHost: supabaseProjectHost,
      timestamp: nowIso(),
    });

    try {
      const { data, error } = await withTimeout(
        supabase
          .from('user_progress')
          .select('*')
          .eq('user_id', userId)
          .maybeSingle(),
        CLOUD_HYDRATION_TIMEOUT_MS,
        'progress_select_timeout'
      );

      if (error) {
        devLog.error(`${LOG_CLOUD} Cloud read error`, {
          runId,
          userId,
          reason,
          code: error?.code,
          message: error?.message,
        });
        devLog.error(`${LOG_DB_READ} error`, {
          runId,
          userId,
          reason,
          code: error?.code,
          message: error?.message,
          timestamp: nowIso(),
        });

        const fallback = isMeaningfulProgress(cachedUserProgress)
          ? cachedUserProgress
          : normalizeProgress(defaultProgress);
        if (runId !== hydrationRunRef.current) return;
        setProgressState(fallback);
        setProgressSource('cloud-cache');
        setIsHydrated(true);
        cloudVerifiedRef.current = false;
        devLog.warn(`${LOG_READ} Fallback to user cache (cloud error)`, {
          runId,
          userId,
          hasMeaningfulCache: isMeaningfulProgress(cachedUserProgress),
        });
        return;
      }

      let normalized = normalizeProgress(defaultProgress);
      hasCloudRow = Boolean(data);

      devLog.info(`${LOG_DB_READ} success`, {
        runId,
        userId,
        reason,
        hasCloudRow,
        xp: data?.xp ?? null,
        level: data?.level ?? null,
        updatedAt: data?.updated_at || null,
        timestamp: nowIso(),
      });

      if (hasCloudRow) {
        const cloudProgress = rowToProgress(data);
        const onboardingLocked = readOnboardingLock(userId);
        const shouldRepairOnboarding =
          !cloudProgress.onboardingComplete &&
          (
            cachedUserProgress.onboardingComplete ||
            onboardingLocked ||
            hasOnboardingCompletionSignals(cloudProgress)
          );
        const resolvedCloudProgress = shouldRepairOnboarding
          ? normalizeProgress({ ...cloudProgress, onboardingComplete: true })
          : cloudProgress;

        // Cloud is the single source of truth for connected users.
        // If a write is actively queued, skip this read to avoid a stale cloud
        // value overwriting pending local state. The write will fire shortly.
        if (saveTimeoutRef.current || atomicXpInFlightRef.current) {
          refreshInFlightRef.current = false;
          if (runId !== hydrationRunRef.current) return;
          setIsSyncing(false);
          devLog.info(`${LOG_CLOUD} Skipping read - write is in flight`, { runId, userId, reason });
          return;
        }

        const cloudSignature = progressSignature(resolvedCloudProgress);
        normalized = resolvedCloudProgress;
        if (runId !== hydrationRunRef.current) return;

        setProgressState(resolvedCloudProgress);
        setProgressSource('cloud');
        writeStorageJson(userCacheKey, resolvedCloudProgress);
        cloudVerifiedRef.current = true;
        setIsHydrated(true);
        lastSavedUserIdRef.current = userId;
        lastSavedSignatureRef.current = cloudSignature;
        pendingLocalChangesRef.current = false;
        if (resolvedCloudProgress.onboardingComplete) {
          writeOnboardingLock(userId, true);
        }

        devLog.info(`${LOG_RESOLVE} cloud_primary`, {
          runId,
          userId,
          reason,
          cloudXP: resolvedCloudProgress.xp,
          cloudLevel: resolvedCloudProgress.level,
          onboardingComplete: resolvedCloudProgress.onboardingComplete,
          onboardingRepairFromLocal: shouldRepairOnboarding,
          timestamp: nowIso(),
        });

        devLog.info(`${LOG_APPLY} connected_apply`, {
          runId,
          userId,
          source: 'cloud',
          reason: 'cloud_primary',
          xp: resolvedCloudProgress.xp,
          level: resolvedCloudProgress.level,
          timestamp: nowIso(),
        });
        devLog.info(`${LOG_PROGRESS_HYDRATION} cloud_applied`, {
          runId,
          userId,
          source: 'cloud',
          onboardingComplete: resolvedCloudProgress.onboardingComplete,
          onboardingRepairFromLocal: shouldRepairOnboarding,
          timestamp: nowIso(),
        });

        devLog.info(`${LOG_READ} Using cloud row as source of truth`, {
          runId,
          userId,
          xp: resolvedCloudProgress.xp,
          level: resolvedCloudProgress.level,
          reason: 'cloud_primary',
        });

        if (shouldRepairOnboarding) {
          devLog.info('[WELCOME_SOURCE] repairing onboarding_complete=true back to cloud', {
            runId,
            userId,
            reason,
            timestamp: nowIso(),
          });
          void persistCloudProgress(resolvedCloudProgress, {
            force: true,
            reason: 'repair_onboarding_complete_from_local',
          });
        }
      } else {
        const guestProgress = normalizeProgress(readStorageJson(GUEST_PROGRESS_KEY, defaultProgress));
        const migrationFlags = readStorageJson(MIGRATION_FLAGS_KEY, {});
        const alreadyMigrated = Boolean(migrationFlags?.[userId]);
        const shouldImportGuest = isMeaningfulProgress(guestProgress) && !alreadyMigrated;

        normalized = shouldImportGuest
          ? guestProgress
          : isMeaningfulProgress(cachedUserProgress)
            ? cachedUserProgress
            : normalizeProgress(defaultProgress);

        if (!normalized.onboardingComplete && hasOnboardingCompletionSignals(normalized)) {
          normalized = normalizeProgress({ ...normalized, onboardingComplete: true });
        }

        devLog.info(`${LOG_READ} Cloud row missing, preparing bootstrap`, {
          runId,
          userId,
          shouldImportGuest,
          alreadyMigrated,
          selectedSource: shouldImportGuest
            ? 'guest-migration'
            : (isMeaningfulProgress(cachedUserProgress) ? 'user-cache' : 'default'),
          xp: normalized.xp,
          level: normalized.level,
          onboardingComplete: normalized.onboardingComplete,
        });

        const ensureResult = await ensureCloudProgressRow(normalized, 'bootstrap_missing_cloud_row');

        if (runId !== hydrationRunRef.current) return;

        if (ensureResult.ok) {
          const resolvedProgress = ensureResult.row
            ? rowToProgress(ensureResult.row)
            : normalized;
          const resolvedSignature = progressSignature(resolvedProgress);

          setProgressState(resolvedProgress);
          setProgressSource('cloud');
          setIsHydrated(true);
          writeStorageJson(userCacheKey, resolvedProgress);
          cloudVerifiedRef.current = true;
          lastSavedUserIdRef.current = userId;
          lastSavedSignatureRef.current = resolvedSignature;
          pendingLocalChangesRef.current = false;
          if (resolvedProgress.onboardingComplete) {
            writeOnboardingLock(userId, true);
          }

          devLog.info(`${LOG_APPLY} bootstrap_apply`, {
            runId,
            userId,
            source: 'cloud-bootstrap',
            method: ensureResult.method,
            xp: resolvedProgress.xp,
            level: resolvedProgress.level,
            timestamp: nowIso(),
          });
          devLog.info(`${LOG_PROGRESS_HYDRATION} bootstrap_cloud_applied`, {
            runId,
            userId,
            method: ensureResult.method,
            onboardingComplete: resolvedProgress.onboardingComplete,
            timestamp: nowIso(),
          });

          devLog.info(`${LOG_CLOUD} Bootstrap sync succeeded`, {
            runId,
            userId,
            method: ensureResult.method,
            xp: resolvedProgress.xp,
            level: resolvedProgress.level,
            onboardingComplete: resolvedProgress.onboardingComplete,
          });
        } else {
          setProgressState(normalized);
          setIsHydrated(true);
          setProgressSource('cloud-cache');
          cloudVerifiedRef.current = false;
          devLog.warn(`${LOG_CLOUD} Bootstrap sync failed, staying in fallback mode`, {
            runId,
            userId,
          });
        }

        if (shouldImportGuest) {
          writeStorageJson(MIGRATION_FLAGS_KEY, {
            ...migrationFlags,
            [userId]: true,
          });
        }
      }

    } catch (err) {
      devLog.error(`${LOG_CLOUD} Unexpected cloud read failure`, {
        runId,
        userId,
        reason,
        error: err?.message || 'unknown',
      });
      devLog.error(`${LOG_DB_READ} failure`, {
        runId,
        userId,
        reason,
        error: err?.message || 'unknown',
        timestamp: nowIso(),
      });
      const fallback = isMeaningfulProgress(cachedUserProgress)
        ? cachedUserProgress
        : normalizeProgress(defaultProgress);
      if (runId !== hydrationRunRef.current) return;
      setProgressState(fallback);
      setProgressSource('cloud-cache');
      setIsHydrated(true);
      cloudVerifiedRef.current = false;
      devLog.warn(`${LOG_READ} Fallback to user cache (unexpected cloud failure)`, {
        runId,
        userId,
        hasMeaningfulCache: isMeaningfulProgress(cachedUserProgress),
      });
    } finally {
      refreshInFlightRef.current = false;
      if (runId !== hydrationRunRef.current) return;
      setIsSyncing(false);
      devLog.info(`${LOG_CLOUD} Cloud read finished`, {
        runId,
        userId,
        reason,
        source: hasCloudRow ? 'cloud' : 'bootstrap',
        cloudVerified: cloudVerifiedRef.current,
      });
    }
  }, [ensureCloudProgressRow, persistCloudProgress, userId]);

  useEffect(() => {
    const currentUserId = userId || null;
    if (userIdRef.current !== currentUserId) {
      userIdRef.current = currentUserId;
      lastSavedSignatureRef.current = '';
      lastSavedUserIdRef.current = null;
      cloudVerifiedRef.current = false;
      pendingLocalChangesRef.current = false;
      refreshInFlightRef.current = false;
      atomicXpInFlightRef.current = false;
    }

    if (authLoading) {
      devLog.info(`${LOG_READ} Waiting for auth hydration`, {
        userId: currentUserId,
      });
      setIsHydrated(false);
      setIsSyncing(false);
      return;
    }

    const runId = hydrationRunRef.current + 1;
    hydrationRunRef.current = runId;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    if (!userId) {
      devLog.info(`${LOG_READ} Hydrating guest progress`, { runId });
      devLog.info(`${LOG_PROGRESS_HYDRATION} hydrate_start_guest`, {
        runId,
        timestamp: nowIso(),
      });
      hydrateGuestProgress(runId);
      return;
    }

    devLog.info(`${LOG_CLOUD} Hydrating cloud progress`, {
      runId,
      userId,
    });
    devLog.info(`${LOG_PROGRESS_HYDRATION} hydrate_start_cloud`, {
      runId,
      userId,
      timestamp: nowIso(),
    });
    hydrateCloudProgress(runId, { reason: 'initial_hydration' });
  }, [authLoading, userId, hydrateGuestProgress, hydrateCloudProgress]);

  useEffect(() => {
    if (!authLoading || isHydrated) return undefined;

    devLog.info(`${LOG_READ} Auth-loading watchdog armed`, {
      timeoutMs: AUTH_LOADING_WATCHDOG_TIMEOUT_MS,
      userId,
    });

    const timer = setTimeout(() => {
      if (!authLoading || isHydrated) return;

      const runId = hydrationRunRef.current + 1;
      hydrationRunRef.current = runId;

      devLog.warn(`${LOG_READ} Auth-loading watchdog fired, forcing temporary local fallback`, {
        runId,
      });
      hydrateGuestProgress(runId, 'auth-timeout-local');
    }, AUTH_LOADING_WATCHDOG_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [authLoading, hydrateGuestProgress, isHydrated, userId]);

  useEffect(() => {
    if (authLoading || isHydrated) return undefined;

    const runId = hydrationRunRef.current;
    devLog.info(`${LOG_READ} Hydration watchdog armed`, {
      runId,
      timeoutMs: HYDRATION_WATCHDOG_TIMEOUT_MS,
      userId,
    });
    const timer = setTimeout(() => {
      if (authLoading || isHydrated || runId !== hydrationRunRef.current) return;

      if (!userId) {
        devLog.warn(`${LOG_READ} Hydration watchdog fired -> guest fallback`, { runId });
        hydrateGuestProgress(runId, 'local-watchdog');
        return;
      }

      const userCacheKey = `${USER_CACHE_KEY_PREFIX}${userId}`;
      const cachedUserProgress = normalizeProgress(readStorageJson(userCacheKey, defaultProgress));
      const fallback = isMeaningfulProgress(cachedUserProgress)
        ? cachedUserProgress
        : normalizeProgress(defaultProgress);

        devLog.warn(`${LOG_READ} Hydration watchdog fired -> cloud timeout fallback`, {
          runId,
          userId,
          hasMeaningfulCache: isMeaningfulProgress(cachedUserProgress),
        });
      setProgressState(fallback);
      setProgressSource(isMeaningfulProgress(cachedUserProgress) ? 'cloud-cache-timeout' : 'cloud-timeout');
      setIsSyncing(false);
      setIsHydrated(true);
      cloudVerifiedRef.current = false;
    }, HYDRATION_WATCHDOG_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [authLoading, hydrateGuestProgress, isHydrated, userId]);

  useEffect(() => {
    if (authLoading || !userId || !isHydrated) return undefined;

    // Refresh only on focus/visibility change - no polling interval.
    // This catches changes made on another device without creating a background
    // race between periodic reads and pending writes.
    const refreshFromCloud = () => {
      if (refreshInFlightRef.current) return;
      if (saveTimeoutRef.current) return;
      if (atomicXpInFlightRef.current) return;

      const runId = hydrationRunRef.current + 1;
      hydrationRunRef.current = runId;
      hydrateCloudProgress(runId, {
        silent: true,
        reason: 'focus_refresh',
      });
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshFromCloud();
      }
    };

    window.addEventListener('focus', refreshFromCloud);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('focus', refreshFromCloud);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [authLoading, userId, isHydrated, hydrateCloudProgress]);

  useEffect(() => {
    if (authLoading || !userId || !isHydrated) return undefined;
    if (cloudVerifiedRef.current) return undefined;

    devLog.info(`${LOG_CLOUD} Strict retry loop active (connected + unverified cloud)`, {
      userId,
      progressSource,
      retryMs: UNVERIFIED_CLOUD_RETRY_MS,
    });

    const retryCloudRead = () => {
      if (refreshInFlightRef.current) return;
      if (atomicXpInFlightRef.current) return;

      const runId = hydrationRunRef.current + 1;
      hydrationRunRef.current = runId;
      hydrateCloudProgress(runId, {
        silent: true,
        reason: 'strict_retry_unverified_cloud',
      });
    };

    const timer = setInterval(retryCloudRead, UNVERIFIED_CLOUD_RETRY_MS);
    window.addEventListener('online', retryCloudRead);

    return () => {
      clearInterval(timer);
      window.removeEventListener('online', retryCloudRead);
    };
  }, [authLoading, userId, isHydrated, progressSource, hydrateCloudProgress]);

  useEffect(() => {
    if (!isHydrated) return;

    if (!userId) {
      writeStorageJson(GUEST_PROGRESS_KEY, progress);
      writeOnboardingLock(null, Boolean(progress.onboardingComplete));
      return;
    }

    const userCacheKey = `${USER_CACHE_KEY_PREFIX}${userId}`;

    if (!cloudVerifiedRef.current) {
      // Connected strict mode: user cache must remain a mirror of verified cloud only.
      // Do not persist unverified connected state locally to avoid durable divergence.
      devLog.warn(`${LOG_SAVE} Skipping connected cache/cloud write - unverified cloud`, {
        userId,
        progressSource,
      });
      return;
    }

    writeStorageJson(userCacheKey, progress);
    writeOnboardingLock(userId, Boolean(progress.onboardingComplete));

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    saveTimeoutRef.current = setTimeout(() => {
      persistCloudProgress(progress, { reason: 'debounced_progress_change' });
    }, 200);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
    };
  }, [isHydrated, persistCloudProgress, progress, progressSource, userId]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    devLog.info(`${LOG_READ} Snapshot`, {
      authLoading,
      userId,
      isHydrated,
      isSyncing,
      progressSource,
      cloudVerified: cloudVerifiedRef.current,
      hasPendingLocalChanges: pendingLocalChangesRef.current,
    });
  }, [authLoading, userId, isHydrated, isSyncing, progressSource]);

  useEffect(() => {
    devLog.info(`${LOG_UI_STATE} applied`, {
      userId,
      authLoading,
      isHydrated,
      progressSource,
      xp: progress.xp,
      level: progress.level,
      pendingLocalChanges: pendingLocalChangesRef.current,
      cloudVerified: cloudVerifiedRef.current,
      timestamp: nowIso(),
    });
  }, [userId, authLoading, isHydrated, progressSource, progress.xp, progress.level]);

  const requestConnectedCloudRefresh = useCallback((reason = 'manual_connected_refresh') => {
    if (!userId) return;
    if (refreshInFlightRef.current) return;
    if (atomicXpInFlightRef.current) return;

    const runId = hydrationRunRef.current + 1;
    hydrationRunRef.current = runId;
    hydrateCloudProgress(runId, {
      silent: true,
      reason,
    });
  }, [userId, hydrateCloudProgress]);

  const persistConnectedMutation = useCallback((nextProgress, reason, options = {}) => {
    const { reconcile = true } = options;
    if (!userId || !nextProgress) return;
    if (!cloudVerifiedRef.current) {
      devLog.warn(`${LOG_SAVE} connected mutation blocked (unverified cloud)`, {
        userId,
        reason,
      });
      requestConnectedCloudRefresh(`${reason}:blocked_unverified_cloud`);
      return;
    }

    void persistCloudProgress(nextProgress, { reason }).then((saved) => {
      if (saved) {
        if (reconcile) {
          requestConnectedCloudRefresh(`${reason}:post_write_reconcile`);
        }
        return;
      }
      devLog.warn(`${LOG_SAVE} connected mutation failed, forcing cloud reconcile`, {
        userId,
        reason,
      });
      requestConnectedCloudRefresh(`${reason}:reconcile_after_failed_write`);
    });
  }, [userId, persistCloudProgress, requestConnectedCloudRefresh]);

  const canMutateConnectedProgress = useCallback((reason) => {
    if (!userId) return true;
    if (cloudVerifiedRef.current) return true;

    devLog.warn(`${LOG_RESOLVE} blocked_connected_local_mutation_unverified_cloud`, {
      userId,
      reason,
      progressSource,
      timestamp: nowIso(),
    });
    requestConnectedCloudRefresh(`${reason}:blocked_unverified_cloud`);
    return false;
  }, [userId, progressSource, requestConnectedCloudRefresh]);

  const addXP = useCallback((amount, meta = {}) => {
    const gain = Number.isFinite(Number(amount)) ? Math.max(0, Math.round(Number(amount))) : 0;
    if (gain <= 0) return false;

    if (userId) {
      const source = meta?.source || 'addXP';
      devLog.info(`${LOG_XP_GAIN} queued_atomic_connected_strict`, {
        userId,
        source,
        gain,
        timestamp: nowIso(),
      });
      void addXpAtomicCloud(gain, source).then((applied) => {
        if (applied) return;
        // Connected strict mode: do not keep local XP-only mutations on failure.
        devLog.warn(`${LOG_XP_GAIN} atomic_failed_connected_strict_no_local_fallback`, {
          userId,
          source,
          gain,
          timestamp: nowIso(),
        });
        requestConnectedCloudRefresh('addXP_rpc_failed_strict');
      });
      return true;
    }

    let changed = false;
    setProgress((prev) => {
      const newXP = prev.xp + gain;
      const oldLevelInfo = getLevelInfo(prev.xp);
      const newLevelInfo = getLevelInfo(newXP);
      const leveledUp = newLevelInfo.currentLevel.level > oldLevelInfo.currentLevel.level;
      changed = true;

      return {
        ...prev,
        xp: newXP,
        level: newLevelInfo.currentLevel.level,
        _leveledUp: leveledUp,
        _newLevel: leveledUp ? newLevelInfo.currentLevel : null,
      };
    });
    return changed;
  }, [setProgress, userId, addXpAtomicCloud, requestConnectedCloudRefresh]);

  const awardDailyXP = useCallback((amount = 10) => {
    const gain = Number.isFinite(Number(amount)) ? Math.max(0, Math.round(Number(amount))) : 10;
    if (gain <= 0) return false;

    const dayToken = getDailyToken();

    if (userId) {
      const previousToken = progressRef.current?.lastDailyXPDate || null;
      if (previousToken === dayToken) return false;

      devLog.info(`${LOG_XP_GAIN} daily_queued_atomic_connected_strict`, {
        userId,
        gain,
        dayToken,
        timestamp: nowIso(),
      });

      void addDailyXpAtomicCloud(gain, dayToken, 'daily_xp').then((applied) => {
        if (applied) return;
        // Connected strict mode: do not keep local daily XP-only mutations on failure.
        devLog.warn(`${LOG_XP_GAIN} daily_atomic_failed_connected_strict_no_local_fallback`, {
          userId,
          gain,
          dayToken,
          timestamp: nowIso(),
        });
        requestConnectedCloudRefresh('daily_xp_rpc_failed_strict');
      });
      return true;
    }

    let granted = false;
    setProgress((prev) => {
      if (prev.lastDailyXPDate === dayToken) {
        return prev;
      }

      const newXP = prev.xp + gain;
      const oldLevelInfo = getLevelInfo(prev.xp);
      const newLevelInfo = getLevelInfo(newXP);
      const leveledUp = newLevelInfo.currentLevel.level > oldLevelInfo.currentLevel.level;
      granted = true;

      return {
        ...prev,
        xp: newXP,
        level: newLevelInfo.currentLevel.level,
        lastDailyXPDate: dayToken,
        _leveledUp: leveledUp,
        _newLevel: leveledUp ? newLevelInfo.currentLevel : null,
      };
    });
    return granted;
  }, [setProgress, userId, addDailyXpAtomicCloud, requestConnectedCloudRefresh]);

  const setActiveChallenge = useCallback((challenge) => {
    if (!canMutateConnectedProgress('set_active_challenge')) return false;
    let snapshot = null;
    setProgress((prev) => {
      const next = { ...prev, activeChallenge: challenge };
      snapshot = next;
      return next;
    });
    if (userId && snapshot) {
      persistConnectedMutation(snapshot, 'set_active_challenge');
    }
    return true;
  }, [canMutateConnectedProgress, persistConnectedMutation, setProgress, userId]);

  const completeChallenge = useCallback(() => {
    const completed = progress.activeChallenge;
    if (!completed) return false;
    if (!canMutateConnectedProgress('complete_challenge')) return false;
    const completedAt = new Date().toISOString();
    let snapshot = null;

    setPendingChallengeScanContext({
      id: completed.id,
      category: completed.category,
      color: completed.color,
      name: completed.name,
      completedAt,
    });

    setProgress((prev) => {
      if (!prev.activeChallenge) return prev;
      const next = {
        ...prev,
        activeChallenge: null,
        challengesCompleted: prev.challengesCompleted + 1,
        completedChallenges: [
          { ...prev.activeChallenge, completedAt },
          ...prev.completedChallenges,
        ].slice(0, 50),
      };
      snapshot = next;
      return next;
    });
    devLog.info(`${LOG_APPLY} challenge_completed`, {
      userId,
      challengeId: completed.id,
      challengesCompleted: snapshot?.challengesCompleted ?? null,
      completedAt,
      timestamp: nowIso(),
    });
    if (userId && snapshot) {
      persistConnectedMutation(snapshot, 'complete_challenge');
    }
    return true;
  }, [progress.activeChallenge, canMutateConnectedProgress, persistConnectedMutation, setProgress, userId]);

  const updateStreak = useCallback(() => {
    if (!canMutateConnectedProgress('update_streak')) return false;
    const today = new Date().toDateString();
    let snapshot = null;
    let changed = false;

    setProgress((prev) => {
      if (prev.lastLoginDate === today) return prev;

      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const isConsecutive = prev.lastLoginDate === yesterday.toDateString();

      const newStreak = isConsecutive ? prev.currentStreak + 1 : 1;
      const newLongest = Math.max(newStreak, prev.longestStreak);

      changed = true;
      const next = {
        ...prev,
        lastLoginDate: today,
        currentStreak: newStreak,
        longestStreak: newLongest,
      };
      snapshot = next;
      return next;
    });
    if (changed && snapshot) {
      devLog.info(`${LOG_APPLY} streak_updated`, {
        userId,
        currentStreak: snapshot.currentStreak,
        longestStreak: snapshot.longestStreak,
        lastLoginDate: snapshot.lastLoginDate,
        timestamp: nowIso(),
      });
      if (userId) {
        persistConnectedMutation(snapshot, 'update_streak');
      }
    }
    return changed;
  }, [canMutateConnectedProgress, setProgress, userId, persistConnectedMutation]);

  const setSelectedBrands = useCallback((brands) => {
    if (!canMutateConnectedProgress('set_selected_brands')) return false;
    const selectedBrands = normalizeSelectedBrands(Array.isArray(brands) ? brands : []);
    let snapshot = null;
    setProgress((prev) => {
      const next = { ...prev, selectedBrands };
      snapshot = next;
      return next;
    });
    if (userId && snapshot) {
      persistConnectedMutation(snapshot, 'set_selected_brands', { reconcile: false });
    }
    return true;
  }, [canMutateConnectedProgress, persistConnectedMutation, setProgress, userId]);

  const setSkillLevel = useCallback((level) => {
    if (!canMutateConnectedProgress('set_skill_level')) return false;
    const skillLevel = level || 'intermediate';
    let snapshot = null;
    setProgress((prev) => {
      const next = { ...prev, skillLevel };
      snapshot = next;
      return next;
    });
    if (userId && snapshot) {
      persistConnectedMutation(snapshot, 'set_skill_level', { reconcile: false });
    }
    return true;
  }, [canMutateConnectedProgress, persistConnectedMutation, setProgress, userId]);

  const completeOnboarding = useCallback(() => {
    if (!canMutateConnectedProgress('complete_onboarding')) return false;
    writeOnboardingLock(userId, true);
    let snapshot = null;
    setProgress((prev) => {
      const next = { ...prev, onboardingComplete: true };
      snapshot = next;
      return next;
    });
    if (userId && snapshot) {
      persistConnectedMutation(snapshot, 'complete_onboarding');
    }
    return true;
  }, [canMutateConnectedProgress, persistConnectedMutation, setProgress, userId]);

  const resetProgress = useCallback(() => {
    if (!canMutateConnectedProgress('reset_progress')) return false;
    const next = normalizeProgress(defaultProgress);
    writeOnboardingLock(userId, false);
    setProgress(next);
    if (userId) {
      persistConnectedMutation(next, 'reset_progress');
    } else {
      writeStorageJson(GUEST_PROGRESS_KEY, next);
    }
    return true;
  }, [canMutateConnectedProgress, persistConnectedMutation, setProgress, userId]);

  const levelInfo = useMemo(() => getLevelInfo(progress.xp), [progress.xp]);

  return {
    progress,
    levelInfo,
    isSyncing,
    progressSource,
    isHydrated,
    authLoading,
    userId,
    addXP,
    awardDailyXP,
    setActiveChallenge,
    completeChallenge,
    updateStreak,
    setSelectedBrands,
    setSkillLevel,
    completeOnboarding,
    resetProgress,
    setProgress,
  };
}

export default useUserProgress;

